import React, { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { userAtom } from '../../store/userAtoms'
import { useTar } from '@/modules/i18n'
import { usePartnerDashboard } from '@/hooks/usePartnerDashboard'
import { useOrganization } from '@/hooks/useOrganization'
import { usePersons } from '@/hooks/usePersons'
import { useTaskDisplayNames } from '@/modules/entityLookups'
import { UserService } from '@duck/api-definitions'
import { Card, CardContent, Skeleton } from '@duck/ui'
import { NoDataEmptyState } from '../EmptyStates'
import { ClipboardDocumentListIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { PartnerDashboardTable } from './PartnerDashboardTable'
import { CohortsOverviewTable } from './CohortsOverviewTable'
import { getCustomStatus, type PartnerDashboardRow } from '@/hooks/TableSettings/partnerDashboardTable.utils'
import { OrgWordmark } from '@/components/OrgWordmark'
import { CreateCohortDialog } from '../PartnerCohorts/CreateCohortDialog'

interface PartnerDashboardProps {
  organizationId?: string
}

export const PartnerDashboard: React.FC<PartnerDashboardProps> = ({ organizationId }) => {
  const user = useAtomValue(userAtom)
  const { t } = useTar()
  const { tP } = usePartnerTar()
  const effectiveOrgId = organizationId ?? user?.organizationId
  const { organization, isLoading: isOrgLoading } = useOrganization(effectiveOrgId)
  const { recentAssignments, cohortsWithStats, isLoading: isLoadingDashboard } =
    usePartnerDashboard(organizationId)

  const allPersonIds = useMemo(() => {
    return Array.from(new Set(recentAssignments.map(ad => ad.personId)))
  }, [recentAssignments])

  const { persons, isLoading: isLoadingPersons } = usePersons(allPersonIds)

  const taskIds = useMemo(() => {
    const ids = new Set<string>()
    recentAssignments.forEach((ad) => ids.add(ad.taskId))
    return Array.from(ids)
  }, [recentAssignments])

  const { getDisplayName, isLoading: isTaskLoading } = useTaskDisplayNames(taskIds, {
    fallback: t('common.na')
  })

  const flatRows = useMemo((): PartnerDashboardRow[] => {
    if (!recentAssignments || recentAssignments.length === 0) return []

    const personsMap = persons.reduce((acc, person) => {
      acc[person.id] = person
      return acc
    }, {} as Record<string, UserService.Person>)

    const rows: PartnerDashboardRow[] = []
    for (const ad of recentAssignments) {
      const person = personsMap[ad.personId]
      if (!person) continue

      rows.push({
        personId: ad.personId,
        assignmentId: ad.assignmentId,
        taskId: ad.taskId,
        taskDisplayName: getDisplayName(ad.taskId),
        email: person.email,
        onboardingStatus: person.onboardingStatus,
        assignmentStatus: ad.status,
        customStatus: getCustomStatus(person.onboardingStatus, ad.status),
        grade: ad.grade,
        feedback: ad.feedback,
        solution: ad.solution,
        startDate: ad.startDate,
        submitDate: ad.submitDate,
        workTime: ad.workTime,
        challengeId: ad.challengeId,
        charts: ad.charts,
        queries: ad.queries,
        assignmentSentDate: ad.createdAt,
        cohortName: ad.cohortName,
      })
    }
    return rows
  }, [recentAssignments, persons, getDisplayName])

  const isLoading = isLoadingDashboard || isLoadingPersons || isOrgLoading
  const hasNoAssignments = recentAssignments.length === 0 || flatRows.length === 0
  const hasNoCohorts = cohortsWithStats.length === 0
  const isViewingOwnDashboard = !organizationId
  const [isCreateCohortDialogOpen, setIsCreateCohortDialogOpen] = useState(false)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {organizationId && organization?.name
              ? `${organization.name} – ${t('partner.dashboard')}`
              : t('partner.dashboard')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {organizationId
              ? t('partner.dashboardDescriptionOrg')
              : t('partner.dashboardDescription', {
                firstName: user?.firstName || '',
                lastName: user?.lastName || ''
              })}
          </p>
        </div>
        {organization?.logoURL && (
          <OrgWordmark
            org={organization}
            className="max-h-12 max-w-48 m-0"
          />
        )}
      </div>

      {isLoading || isTaskLoading ? (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <div className="min-w-full border border-border rounded-md divide-y divide-border">
              <div className="bg-muted/15 px-6 py-3">
                <div className="flex items-center">
                  <div className="flex gap-6">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>

              <div className="bg-card divide-y divide-border">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center px-6 py-4 gap-6"
                  >
                    <div className="flex items-center gap-4 min-w-[220px]">
                      <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <Skeleton className="h-5 w-5 rounded-full" />
                      </div>
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-24" />
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-3 gap-6">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-6 w-24 rounded-full" />
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {hasNoCohorts ? (
            <Card>
              <CardContent className="pt-6">
                <NoDataEmptyState
                  icon={UserGroupIcon}
                  title={tP('noCohortsYet')}
                  description={tP('noCohortsDescription')}
                  buttonText={isViewingOwnDashboard ? tP('createCohort') : undefined}
                  onButtonClick={
                    isViewingOwnDashboard
                      ? () => setIsCreateCohortDialogOpen(true)
                      : undefined
                  }
                />
              </CardContent>
            </Card>
          ) : (
            <>
              <CohortsOverviewTable
                cohortsWithStats={cohortsWithStats}
                organizationId={organizationId}
              />
              {hasNoAssignments ? (
                <Card>
                  <CardContent className="pt-6">
                    <NoDataEmptyState
                      icon={ClipboardDocumentListIcon}
                      title={t('partner.noAssignmentsYet')}
                      description={tP('noAssignmentsDescription')}
                    />
                  </CardContent>
                </Card>
              ) : (
                <PartnerDashboardTable rows={flatRows} persons={persons} />
              )}
            </>
          )}
        </div>
      )}

      {isViewingOwnDashboard && (
        <CreateCohortDialog
          isOpen={isCreateCohortDialogOpen}
          onClose={() => setIsCreateCohortDialogOpen(false)}
        />
      )}
    </div>
  )
}
